/**
 * Navigation Component
 *
 * Primary navigation bar. On the homepage, "Shop Now" scrolls to products.
 * On other pages, "Shop Now" navigates to the homepage product section.
 */

import React, { useState } from 'react';
import { CartButton } from './CartButton';
import { Menu, X } from 'lucide-react';

interface NavigationProps {
  scrollToProduct?: () => void;
}

export const Navigation: React.FC<NavigationProps> = ({ scrollToProduct }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleShopNow = () => {
    if (scrollToProduct) {
      scrollToProduct();
    } else {
      window.location.href = '/#product-section';
    }
  };

  const menuItems = [
    { label: 'Shop Now', href: undefined, onClick: handleShopNow },
    { label: 'About', href: '/about' },
    { label: 'FAQs', href: '/faqs' },
    { label: 'Contact', href: '/contact' },
  ];

  const legalLinks = [
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Terms of Service', href: '/terms' },
    { label: 'Returns', href: '/returns' },
    { label: 'Disclaimer', href: '/disclaimer' },
  ];

  return (
    <nav className="fixed w-full z-50 bg-black/60 backdrop-blur-lg border-b border-white/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-24">
          {/* Brand Logo */}
          <div className="flex items-center">
            <a href="/" aria-label="RaveRemedy - Premium Post-Rave Recovery Supplements">
              <img
                src="/01.png"
                alt="RaveRemedy logo - Premium post-rave recovery supplements for festival-goers"
                className="h-10 md:h-16 w-auto object-contain"
              />
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {menuItems.map((item) => (
              <button
                key={item.label}
                onClick={() => item.href ? window.location.href = item.href : item.onClick?.()}
                className="text-white hover:text-[#ff00ff] transition-colors"
              >
                {item.label}
              </button>
            ))}
            <CartButton />
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <CartButton />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-white p-1 hover:text-[#ff00ff] transition-colors"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-4 pt-2 pb-4 space-y-1 bg-black/90 backdrop-blur-lg border-b border-white/20">
            {menuItems.map((item) => (
              <button
                key={item.label}
                onClick={() => {
                  setIsMenuOpen(false);
                  item.href ? window.location.href = item.href : item.onClick?.();
                }}
                className="block w-full text-left px-3 py-2 text-white hover:text-[#ff00ff] transition-colors"
              >
                {item.label}
              </button>
            ))}

            <div className="border-t border-white/10 mt-2 pt-2">
              <p className="px-3 text-xs text-gray-400 mb-1">Legal</p>
              <div className="grid grid-cols-2 gap-1">
                {legalLinks.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={() => setIsMenuOpen(false)}
                    className="block w-full text-left px-3 py-1 text-xs text-white hover:text-[#ff00ff] transition-colors"
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
